import React, { useState, useEffect, useCallback } from "react";
import {
  Box, MenuItem, TextField, Typography, Chip, Collapse, CircularProgress, Skeleton,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Tooltip,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CategoryIcon from "@mui/icons-material/Category";
import AddIcon from "@mui/icons-material/Add";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

interface CatOption { id: number; name: string; has_children: boolean; image?: string; parent_ids?: number[]; }
interface LevelSelection { level: number; id: number; name: string; options: CatOption[]; }

interface Props {
  onCategoryChange: (categoryId: number | null, breadcrumb: string[]) => void;
  initialCategoryId?: number | null;
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2, fontSize: 13, bgcolor: "#fff",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#00838f", borderWidth: 1.5 },
  },
};

const CategoryCascade: React.FC<Props> = ({ onCategoryChange, initialCategoryId }) => {
  const [levels, setLevels] = useState<LevelSelection[]>([]);
  const [loadingLevel, setLoadingLevel] = useState<number | null>(null);
  const [rootOptions, setRootOptions] = useState<CatOption[]>([]);
  const [rootLoading, setRootLoading] = useState(true);

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  // Load roots on mount + pre-select initial category
  useEffect(() => {
    (async () => {
      setRootLoading(true);
      try {
        const res = await fetch(`${API_URL}/categories/roots`, { headers: getHeaders() });
        const json = await res.json();
        if (json.success) {
          const roots = json.data || [];
          setRootOptions(roots);

          // Pre-select initial category if provided
          if (initialCategoryId && roots.length > 0) {
            // Check if the initial category is a root
            const rootMatch = roots.find((r: CatOption) => r.id === initialCategoryId);
            if (rootMatch) {
              setLevels([{ level: 0, id: rootMatch.id, name: rootMatch.name, options: roots }]);
              onCategoryChange(rootMatch.id, [rootMatch.name]);
              // Load children if any
              if (rootMatch.has_children) {
                const children = await fetchChildren(rootMatch.id);
                if (children.length > 0) { setPendingChildren(children); setPendingLevel(1); }
              }
            } else {
              // It's a deeper category — need to find which root it belongs to
              // Try each root to find the chain
              for (const root of roots) {
                const children = await fetchChildren(root.id);
                const childMatch = children.find((c: CatOption) => c.id === initialCategoryId);
                if (childMatch) {
                  setLevels([
                    { level: 0, id: root.id, name: root.name, options: roots },
                    { level: 1, id: childMatch.id, name: childMatch.name, options: children },
                  ]);
                  onCategoryChange(childMatch.id, [root.name, childMatch.name]);
                  if (childMatch.has_children) {
                    const subChildren = await fetchChildren(childMatch.id);
                    if (subChildren.length > 0) { setPendingChildren(subChildren); setPendingLevel(2); }
                  }
                  break;
                }
              }
            }
          }
        }
      } catch {} finally { setRootLoading(false); }
    })();
  }, [initialCategoryId]);

  // Fetch children for a parent
  const fetchChildren = useCallback(async (parentId: number): Promise<CatOption[]> => {
    try {
      const res = await fetch(`${API_URL}/categories/children/${parentId}`, { headers: getHeaders() });
      const json = await res.json();
      return json.success ? json.data || [] : [];
    } catch { return []; }
  }, []);

  // Handle root selection
  const handleRootSelect = async (categoryId: number) => {
    const selected = rootOptions.find(o => o.id === categoryId);
    if (!selected) return;

    const newLevels: LevelSelection[] = [{ level: 0, id: categoryId, name: selected.name, options: rootOptions }];

    if (selected.has_children) {
      setLoadingLevel(1);
      const children = await fetchChildren(categoryId);
      setLoadingLevel(null);
      if (children.length > 0) {
        // Don't add level yet — wait for user to select
        setLevels(newLevels);
        // Store pending children
        setPendingChildren(children);
        setPendingLevel(1);
        onCategoryChange(categoryId, [selected.name]);
        return;
      }
    }

    setLevels(newLevels);
    setPendingChildren([]);
    setPendingLevel(null);
    onCategoryChange(categoryId, [selected.name]);
  };

  const [pendingChildren, setPendingChildren] = useState<CatOption[]>([]);
  const [pendingLevel, setPendingLevel] = useState<number | null>(null);

  // ─── Quick-add modal ──────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [createLevel, setCreateLevel] = useState<number>(0);
  const [createName, setCreateName] = useState("");
  const [createError, setCreateError] = useState("");
  const [creating, setCreating] = useState(false);

  // Build parent_ids chain for a given level (0 = root → no parents)
  const parentIdsForLevel = (level: number): number[] =>
    level <= 0 ? [] : levels.slice(0, level).map((l) => l.id);

  const openCreate = (level: number) => {
    setCreateLevel(level);
    setCreateName("");
    setCreateError("");
    setCreateOpen(true);
  };

  const handleCreateCategory = async () => {
    const name = createName.trim();
    if (!name) { setCreateError("Name is required"); return; }
    setCreating(true);
    setCreateError("");
    try {
      const parents = parentIdsForLevel(createLevel);
      const res = await fetch(`${API_URL}/category`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          categoryName: name,
          parent_ids: parents.length > 0 ? parents : undefined,
          isActive: true,
        }),
      });
      const text = await res.text();
      let json: any = null;
      try { json = JSON.parse(text); } catch {
        throw new Error(/^\s*<!DOCTYPE|^\s*<html/i.test(text) ? "Server returned HTML — auth expired or endpoint missing." : `Invalid response (status ${res.status})`);
      }
      if (!json?.status) {
        setCreateError(json?.message || "Failed to create category");
        return;
      }

      const createdId: number | undefined = json?.data?.id ?? json?.id;
      const lower = name.toLowerCase();

      if (createLevel === 0) {
        // Refresh roots, then auto-select the new one
        const r = await fetch(`${API_URL}/categories/roots`, { headers: getHeaders() });
        const rj = await r.json();
        const roots: CatOption[] = rj?.success ? (rj.data || []) : [];
        setRootOptions(roots);
        const created = roots.find((o) => o.id === createdId) || roots.find((o) => o.name?.toLowerCase() === lower);
        if (created) {
          setLevels([{ level: 0, id: created.id, name: created.name, options: roots }]);
          setPendingChildren([]); setPendingLevel(null);
          onCategoryChange(created.id, [created.name]);
        }
      } else {
        // Sub-category: parent is the level above
        const parentId = levels[createLevel - 1]?.id;
        if (parentId != null) {
          const children = await fetchChildren(parentId);
          const created = children.find((o) => o.id === createdId) || children.find((o) => o.name?.toLowerCase() === lower);
          // Trim deeper levels and append the new one
          const kept = levels.slice(0, createLevel);
          if (created) {
            const newLevels: LevelSelection[] = [...kept, { level: createLevel, id: created.id, name: created.name, options: children }];
            setLevels(newLevels);
            setPendingChildren([]); setPendingLevel(null);
            // If the new one has children, surface the next picker
            if (created.has_children) {
              const sub = await fetchChildren(created.id);
              if (sub.length > 0) { setPendingChildren(sub); setPendingLevel(createLevel + 1); }
            }
            onCategoryChange(created.id, newLevels.map((l) => l.name));
          } else {
            // Couldn't locate the row — at least surface refreshed options
            setPendingChildren(children);
            setPendingLevel(createLevel);
          }
        }
      }

      setCreateOpen(false);
      setCreateName("");
    } catch (e: any) {
      setCreateError(e?.message || "Failed to create category");
    } finally {
      setCreating(false);
    }
  };

  // Handle sub-level selection
  const handleLevelSelect = async (level: number, categoryId: number) => {
    const options = level === (levels.length) ? pendingChildren : levels[level]?.options || [];
    const selected = options.find(o => o.id === categoryId);
    if (!selected) return;

    // Keep levels up to this point
    const kept = levels.slice(0, level);
    const newLevel: LevelSelection = { level, id: categoryId, name: selected.name, options };
    const newLevels = [...kept, newLevel];

    if (selected.has_children) {
      setLoadingLevel(level + 1);
      const children = await fetchChildren(categoryId);
      setLoadingLevel(null);
      if (children.length > 0) {
        setLevels(newLevels);
        setPendingChildren(children);
        setPendingLevel(level + 1);
        const breadcrumb = newLevels.map(l => l.name);
        onCategoryChange(categoryId, breadcrumb);
        return;
      }
    }

    setLevels(newLevels);
    setPendingChildren([]);
    setPendingLevel(null);
    const breadcrumb = newLevels.map(l => l.name);
    onCategoryChange(categoryId, breadcrumb);
  };

  // Reset all
  const handleClear = () => {
    setLevels([]);
    setPendingChildren([]);
    setPendingLevel(null);
    onCategoryChange(null, []);
  };

  const breadcrumb = levels.map(l => l.name);
  const lastSelectedId = levels.length > 0 ? levels[levels.length - 1].id : null;
  const levelLabels = ["Category", "Sub Category", "Sub Sub Category", "Level 4", "Level 5"];

  return (
    <Box>
      {/* Breadcrumb */}
      {breadcrumb.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2, flexWrap: "wrap" }}>
          {breadcrumb.map((name, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRightIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />}
              <Chip label={name} size="small"
                sx={{
                  height: 26, fontSize: 11, fontWeight: 600,
                  bgcolor: i === breadcrumb.length - 1 ? "#00838f" : "#f1f5f9",
                  color: i === breadcrumb.length - 1 ? "#fff" : "#334155",
                  textTransform: "capitalize",
                }} />
            </React.Fragment>
          ))}
          <Chip label="×" size="small" onClick={handleClear}
            sx={{ height: 22, width: 22, fontSize: 12, cursor: "pointer", bgcolor: "#fef2f2", color: "#ef4444", "& .MuiChip-label": { px: 0 } }} />
        </Box>
      )}

      {/* Root dropdown */}
      <Box sx={{ mb: pendingLevel !== null || levels.length === 0 ? 0 : 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Category</Typography>
          <Tooltip title="Create new category">
            <IconButton size="small" onClick={() => openCreate(0)} sx={{ width: 22, height: 22, "&:hover": { bgcolor: "rgba(0,131,143,0.08)" } }}>
              <AddIcon sx={{ fontSize: 14, color: "#00838f" }} />
            </IconButton>
          </Tooltip>
        </Box>
        {rootLoading ? (
          <Skeleton variant="rounded" height={40} sx={{ borderRadius: 2 }} />
        ) : (
          <TextField select fullWidth size="small"
            value={levels.length > 0 ? levels[0].id : ""}
            onChange={e => handleRootSelect(Number(e.target.value))}
            sx={inputSx}>
            <MenuItem value="" disabled>
              <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>Select Category</Typography>
            </MenuItem>
            {rootOptions.map(o => (
              <MenuItem key={o.id} value={o.id}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography fontSize={13} sx={{ textTransform: "capitalize" }}>{o.name}</Typography>
                  {o.has_children && <ChevronRightIcon sx={{ fontSize: 14, color: "#cbd5e1", ml: "auto" }} />}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>

      {/* Dynamic child dropdowns */}
      {levels.map((level, idx) => {
        // Show next dropdown if this level's selection has children
        const nextLevel = idx + 1;
        const isNextPending = pendingLevel === nextLevel;
        const isNextLoading = loadingLevel === nextLevel;

        if (!isNextPending && !isNextLoading) return null;

        const options = isNextPending ? pendingChildren : [];
        const selectedAtNext = levels[nextLevel];

        return (
          <Collapse in key={nextLevel} timeout={300}>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>
                  {levelLabels[nextLevel] || `Level ${nextLevel + 1}`}
                </Typography>
                <Tooltip title={levels.length < nextLevel ? "Select category first" : `Create new ${(levelLabels[nextLevel] || "category").toLowerCase()}`}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => openCreate(nextLevel)}
                      disabled={levels.length < nextLevel}
                      sx={{ width: 22, height: 22, "&:hover": { bgcolor: "rgba(0,131,143,0.08)" } }}
                    >
                      <AddIcon sx={{ fontSize: 14, color: "#00838f" }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              {isNextLoading ? (
                <Skeleton variant="rounded" height={40} sx={{ borderRadius: 2 }} />
              ) : (
                <TextField select fullWidth size="small"
                  value={selectedAtNext?.id || ""}
                  onChange={e => handleLevelSelect(nextLevel, Number(e.target.value))}
                  sx={inputSx}>
                  <MenuItem value="" disabled>
                    <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>
                      Select {levelLabels[nextLevel] || `Level ${nextLevel + 1}`}
                    </Typography>
                  </MenuItem>
                  {options.map(o => (
                    <MenuItem key={o.id} value={o.id}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography fontSize={13} sx={{ textTransform: "capitalize" }}>{o.name}</Typography>
                        {o.has_children && <ChevronRightIcon sx={{ fontSize: 14, color: "#cbd5e1", ml: "auto" }} />}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
          </Collapse>
        );
      })}

      {/* ── Quick-add Category modal ─────────────────────────────────── */}
      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        PaperProps={{ sx: { borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 10px 30px rgba(15,23,42,0.12)", minWidth: 380 } }}
        BackdropProps={{ sx: { bgcolor: "rgba(0,0,0,0.4)" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a", pb: 1 }}>
          {createLevel === 0 ? "Create Category" : `Create ${levelLabels[createLevel] || `Level ${createLevel + 1}`}`}
        </DialogTitle>
        <DialogContent>
          {createLevel > 0 && (
            <Box sx={{ mb: 1.5, p: 1, bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
              <Typography fontSize={10} fontWeight={700} color="#64748b" textTransform="uppercase" letterSpacing={0.5} mb={0.25}>Parent</Typography>
              <Typography fontSize={12} color="#334155">
                {levels.slice(0, createLevel).map((l) => l.name).join(" › ") || "—"}
              </Typography>
            </Box>
          )}
          <Typography fontSize={11} fontWeight={700} color="#64748b" textTransform="uppercase" letterSpacing={0.5} mb={0.5} mt={createLevel > 0 ? 0 : 0.5}>
            {createLevel === 0 ? "Category Name" : "Sub Category Name"} *
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g. Veggies"
            value={createName}
            onChange={(e) => { setCreateName(e.target.value); setCreateError(""); }}
            disabled={creating}
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter" && createName.trim() && !creating) { e.preventDefault(); handleCreateCategory(); } }}
            sx={inputSx}
          />
          {createError && (
            <Typography sx={{ fontSize: 11, color: "#ef4444", mt: 1, fontWeight: 600 }}>{createError}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.25, gap: 1 }}>
          <Button
            onClick={() => setCreateOpen(false)}
            disabled={creating}
            variant="outlined"
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, borderColor: "#e2e8f0", color: "#475569", "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCategory}
            disabled={creating || !createName.trim()}
            variant="contained"
            disableElevation
            sx={{
              borderRadius: "8px", textTransform: "none", fontWeight: 700,
              background: "linear-gradient(135deg, #00838f 0%, #006064 100%)",
              "&:hover": { background: "linear-gradient(135deg, #006064 0%, #004d54 100%)" },
            }}
          >
            {creating ? "Creating..." : (createLevel === 0 ? "Create Category" : "Create Sub Category")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryCascade;
